import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { IGetOneResponse } from '../../shared/model/interface/crud-response-interface.model';
import { IConfigurableFormFieldEntryValue } from '../../shared/component/configurable-form-fields/configurable-form-fields.model';
import {
  IConfigurableFieldsEditData,
  IConfigurableFieldsData,
  IConfigurableFieldsDataDetail,
  IConfigurableFieldsEntryContext,
  IConfigurableFieldsAddData,
} from './form-entry-configurable-fields.model';
import { IIssuerAndReason } from '../../shared/component/before-action-preparer/before-action-preparer.model';

@Injectable({
  providedIn: 'root',
})
export class FormEntryConfigurableFieldsService {
  private readonly URLS = {
    CONFIGURABLE_FIELDS_DATA: `${this.baseUrl}/configurable-fields-data/`,
    FIELD_CONTROL: `${this.baseUrl}/configurable-fields-data/field-control/`,
    GET_CONFIGURABLE_FIELDS: `${this.baseUrl}/configurable-fields-data/get-configurable-fields/`,
  };

  constructor(public http: HttpClient, @Inject('API_BASE_URL') private readonly baseUrl: string) {}

  public checkHasConfigurableFields(
    logbookId: number,
    formId: number,
    issuerAndReason: IIssuerAndReason | null,
  ): Observable<IGetOneResponse<boolean>> {
    return this.http.post<IGetOneResponse<boolean>>(this.URLS.FIELD_CONTROL, {
      issuer: issuerAndReason?.issuer,
      payload: { logbookId, formId },
    });
  }

  public getConfigurableFieldsAddData(
    logbookId: number,
    formId: number,
    issuerAndReason: IIssuerAndReason | null,
  ): Observable<IGetOneResponse<IConfigurableFieldsAddData>> {
    return this.http
      .post<IGetOneResponse<IConfigurableFieldsEntryContext>>(this.URLS.GET_CONFIGURABLE_FIELDS, {
        issuer: issuerAndReason?.issuer,
        payload: { logbookId, formId },
      })
      .pipe(map(FormEntryConfigurableFieldsService.toAddData));
  }

  private static toAddData(
    response: IGetOneResponse<IConfigurableFieldsEntryContext>,
  ): IGetOneResponse<IConfigurableFieldsAddData> {
    return {
      ...response,
      data: {
        fields: response.data?.fieldVersionItems ?? [],
        selectableFieldsMeta: response.data?.selectableFieldsMeta ?? [],
        fieldVersionId: response.data?.fieldVersionId ?? null,
      },
    };
  }

  public getConfigurableFieldsEditData(
    configurableFieldsDataId: number,
    issuerAndReason: IIssuerAndReason | null,
  ): Observable<IGetOneResponse<IConfigurableFieldsEditData>> {
    return this.http
      .post<IGetOneResponse<IConfigurableFieldsDataDetail>>(
        `${this.URLS.CONFIGURABLE_FIELDS_DATA}${configurableFieldsDataId}/`,
        { issuer: issuerAndReason?.issuer, payload: {} },
      )
      .pipe(map(FormEntryConfigurableFieldsService.toEditData));
  }

  private static toEditData(
    response: IGetOneResponse<IConfigurableFieldsDataDetail>,
  ): IGetOneResponse<IConfigurableFieldsEditData> {
    return {
      ...response,
      data: {
        fields: response.data?.fieldVersionItems ?? [],
        selectableFieldsMeta: response.data?.selectableFieldsMeta ?? [],
        values: response.data?.configurableFieldsData?.meta ?? null,
      },
    };
  }

  public createFormEntryWithConfigurableFields(
    logbookId: number,
    formId: number,
    fieldVersionId: number | null,
    values: IConfigurableFormFieldEntryValue[],
    issuerAndReason: IIssuerAndReason | null,
    confirmCanceledUniqueValues: boolean,
  ): Observable<IGetOneResponse<IConfigurableFieldsData>> {
    return this.http.post<IGetOneResponse<IConfigurableFieldsData>>(this.URLS.CONFIGURABLE_FIELDS_DATA, {
      issuer: issuerAndReason?.issuer,
      payload: {
        logbookId,
        formId,
        fieldVersionId,
        meta: values,
        confirmCanceledUniqueValues,
      },
    });
  }

  public updateFormEntryConfigurableFields(
    configurableFieldsDataId: number,
    values: IConfigurableFormFieldEntryValue[],
    issuerAndReason: IIssuerAndReason | null,
    confirmCanceledUniqueValues: boolean,
  ): Observable<IGetOneResponse<IConfigurableFieldsData>> {
    return this.http.put<IGetOneResponse<IConfigurableFieldsData>>(
      `${this.URLS.CONFIGURABLE_FIELDS_DATA}${configurableFieldsDataId}/`,
      {
        issuer: issuerAndReason?.issuer,
        payload: {
          meta: values,
          reason: issuerAndReason?.reason,
          confirmCanceledUniqueValues,
        },
      },
    );
  }
}
